import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "../../../../components/Loader";
import { formatServiceLevelSummary } from "../../../../libs/labels";

const statusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const statusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "open") return "warning";
  if (value === "approved") return "info";
  if (value === "processing") return "secondary";
  if (value === "paid") return "success";
  return "default";
};

const formatAddress = (address) => {
  if (!address) return "";
  const { line1, line2, city, state, postal_code, country } = address;
  return [line1, line2, city, state, postal_code, country].filter(Boolean).join(", ");
};

const resolveInvoiceIssueDate = (invoice = {}) => {
  const createdAt = invoice?.createdAt;
  if (typeof createdAt?.toDate === "function") {
    const dt = createdAt.toDate();
    if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt;
  }
  const createdAtSec = Number(createdAt?.seconds ?? createdAt?._seconds ?? 0);
  if (createdAtSec > 0) return new Date(createdAtSec * 1000);
  const stripeCreatedSec = Number(invoice?.created || 0);
  if (stripeCreatedSec > 0) return new Date(stripeCreatedSec * 1000);
  return null;
};

const formatServiceSummary = (metadata = {}) => {
  return formatServiceLevelSummary(metadata.service, metadata.level);
};

const buildInvoicePdf = (invoice) => {
  if (!invoice) return;
  const {
    invoiceId,
    dueDate,
    seller = {},
    buyer = {},
    entries = [],
    metadata = {},
    amount,
    hoursWorked,
    month,
  } = invoice;

  const doc = new jsPDF("p", "pt", "a4");
  const padding = 40;

  doc.setFontSize(20);
  doc.text("Invoice", padding, 50);

  const createdDate = resolveInvoiceIssueDate(invoice);
  const due = dueDate ? new Date(dueDate) : null;

  doc.setFontSize(10);
  autoTable(doc, {
    startY: 70,
    margin: { left: padding },
    columnStyles: { 0: { fontStyle: "bold", minCellWidth: 80, cellWidth: 80, cellPadding: 0 }, 1: { halign: "left", cellPadding: 0 } },
    body: [
      ["Invoice Id", `${invoiceId || "—"}`],
      ["Date of issue", `${createdDate ? createdDate.toDateString() : "—"}`],
      ["Due Date", `${due ? due.toDateString() : "—"}`],
    ],
    theme: "plain",
    tableWidth: 300,
  });

  doc.text("From:", padding, 135);
  doc.text(seller.name || "—", padding, 150);
  doc.text(formatAddress(seller.address), padding, 162, { maxWidth: 200 });

  doc.text("Bill to:", 320, 135);
  doc.text(buyer.name || "—", 320, 150);
  doc.text(formatAddress(buyer.address), 320, 162, { maxWidth: 200 });

  autoTable(doc, {
    startY: 200,
    margin: { left: padding },
    columnStyles: { 0: { fontStyle: "bold", minCellWidth: 80, cellWidth: 80, cellPadding: 0 }, 1: { halign: "left", cellPadding: 0 } },
    body: [
      ["Month", `${month ? dayjs(month, "YYYY-MM").format("MMMM YYYY") : "—"}`],
      ["Service", formatServiceSummary(metadata)],
    ],
    theme: "plain",
    tableWidth: 200,
  });

  const headers = [["#", "Date", "Hours", "Rate", "Amount"]];
  const data = entries.map((entry, i) => [
    i + 1,
    entry.date,
    entry.hours,
    `$${(Number(entry.rate || 0) / 100).toFixed(2)}`,
    `$${((Number(entry.hours || 0) * Number(entry.rate || 0)) / 100).toFixed(2)}`,
  ]);

  autoTable(doc, {
    startY: 245,
    head: headers,
    body: data,
    margin: { left: padding },
    theme: "grid",
    styles: { fontSize: 10 },
  });

  autoTable(doc, {
    margin: { left: doc.internal.pageSize.getWidth() - 280, top: 10 },
    columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } },
    body: [
      ["Total hours", `${Number(hoursWorked || 0)}h`],
      ["Subtotal", `$${(Number(amount || 0) / 100).toFixed(2)}`],
      ["Total", `$${(Number(amount || 0) / 100).toFixed(2)}`],
      ["Amount due", `$${(Number(amount || 0) / 100).toFixed(2)}`],
    ],
    theme: "grid",
    tableWidth: 240,
  });

  doc.save(`${invoiceId || "invoice"}.pdf`);
};

const InvoiceTemplate = ({ invoice, formatCurrency }) => {
  if (!invoice) return null;
  const {
    invoiceId,
    dueDate,
    seller = {},
    buyer = {},
    entries = [],
    metadata = {},
    amount,
    hoursWorked,
    month,
  } = invoice;
  const createdDate = resolveInvoiceIssueDate(invoice);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <GridRow
        left={
          <>
            <Typography variant="h5" fontWeight="bold">Invoice</Typography>
            <Typography variant="body2"><strong>Invoice ID:</strong> {invoiceId || "—"}</Typography>
            <Typography variant="body2"><strong>Date of issue:</strong> {createdDate ? createdDate.toDateString() : "—"}</Typography>
            <Typography variant="body2"><strong>Due Date:</strong> {dueDate ? new Date(dueDate).toDateString() : "—"}</Typography>
          </>
        }
        right={
          <Button variant="outlined" onClick={() => buildInvoicePdf(invoice)} startIcon={<DownloadIcon />}>
            Download
          </Button>
        }
      />

      <GridRow
        left={
          <>
            <Typography variant="subtitle1" fontWeight="bold">From</Typography>
            <Typography variant="body2">{seller.name || "—"}</Typography>
            <Typography variant="body2">{formatAddress(seller.address) || "—"}</Typography>
          </>
        }
        right={
          <>
            <Typography variant="subtitle1" fontWeight="bold">Bill to</Typography>
            <Typography variant="body2">{buyer.name || "—"}</Typography>
            <Typography variant="body2">{formatAddress(buyer.address) || "—"}</Typography>
          </>
        }
      />

      <Box mb={2}>
        <Typography variant="body2"><strong>Month:</strong> {month ? dayjs(month, "YYYY-MM").format("MMMM YYYY") : "—"}</Typography>
        <Typography variant="body2"><strong>Service:</strong> {formatServiceSummary(metadata)}</Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={`${entry.date}-${index}`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{entry.date || "—"}</TableCell>
                <TableCell>{Number(entry.hours || 0).toFixed(2)}</TableCell>
                <TableCell>{formatCurrency(Number(entry.rate || 0), metadata.currency || "usd")}</TableCell>
                <TableCell>{formatCurrency(Number(entry.hours || 0) * Number(entry.rate || 0), metadata.currency || "usd")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2">Total Hours: <strong>{Number(hoursWorked || 0)}h</strong></Typography>
        <Typography variant="body2">Total Amount: <strong>{formatCurrency(Number(amount || 0), metadata.currency || "usd")}</strong></Typography>
      </Box>
    </Box>
  );
};

const GridRow = ({ left, right }) => (
  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "flex-start" }} spacing={2} mb={2}>
    <Box>{left}</Box>
    <Box>{right}</Box>
  </Stack>
);

const SlideConfirm = ({
  disabled = false,
  helper,
  label,
  onConfirm,
  slideLabel,
  tone = "success",
}) => {
  const [value, setValue] = useState(0);
  const isSuccess = tone === "success";
  const baseColor = isSuccess ? "#1f7a35" : "#1976d2";
  const trackLabel = value >= 92 ? `Release to ${String(label || "confirm").toLowerCase()}` : slideLabel.replace(/^Slide/i, "Swipe");

  useEffect(() => {
    if (!disabled) setValue(0);
  }, [disabled]);

  const commit = async () => {
    if (disabled) return;
    if (value < 92) {
      setValue(0);
      return;
    }
    await onConfirm?.();
    setValue(0);
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      {!!helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helper}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          height: 64,
          borderRadius: 999,
          overflow: "visible",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: disabled ? "grey.100" : "grey.100",
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.common.white}`,
          opacity: disabled ? 0.65 : 1,
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 6,
            top: 6,
            bottom: 6,
            width: value > 0 ? `calc(${value}% - 12px)` : 0,
            minWidth: value > 0 ? 52 : 0,
            borderRadius: 999,
            bgcolor: disabled ? "grey.200" : "rgba(25, 118, 210, 0.10)",
            background: disabled
              ? undefined
              : `linear-gradient(90deg, ${baseColor}22, ${baseColor}10)`,
            transition: "width 120ms ease",
          }}
        />
        <Typography
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: disabled ? "text.disabled" : "text.primary",
            fontWeight: 850,
            letterSpacing: 0.1,
            pointerEvents: "none",
            pl: { xs: 8, sm: 0 },
          }}
        >
          {disabled ? <Loader size={20} /> : trackLabel}
        </Typography>
        <Box
          sx={{
            position: "absolute",
            left: value > 0 ? `${value}%` : 0,
            top: 0,
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: disabled ? "grey.400" : baseColor,
            color: "common.white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: disabled ? "none" : "0 8px 22px rgba(25, 118, 210, 0.28)",
            transform: value > 70 ? "translateX(-100%)" : "none",
            transition: "left 120ms ease, transform 120ms ease, box-shadow 120ms ease",
            pointerEvents: "none",
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 34 }} />
        </Box>
        <Box
          component="input"
          type="range"
          min={0}
          max={100}
          value={value}
          disabled={disabled}
          aria-label={slideLabel}
          onChange={(event) => setValue(Number(event.target.value))}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={(event) => {
            if (event.key === "Enter" || event.key === " ") commit();
          }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: disabled ? "not-allowed" : "grab",
          }}
        />
      </Box>
    </Box>
  );
};

const Invoices = ({
  loading,
  invoices = [],
  busyById = {},
  onApprove,
  onMoveToProcessing,
  resolveContractorName,
  formatCurrency,
  formatDate,
  statusFilter: controlledStatusFilter,
  onStatusFilterChange,
  showStatusFilter = true,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState("open");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const sorted = useMemo(() => [...invoices].sort((a, b) => {
    const aIssue = resolveInvoiceIssueDate(a)?.getTime?.() || 0;
    const bIssue = resolveInvoiceIssueDate(b)?.getTime?.() || 0;
    if (aIssue !== bIssue) return bIssue - aIssue;
    const aSec = Number(a.updatedAt?.seconds || a.updatedAt?._seconds || 0);
    const bSec = Number(b.updatedAt?.seconds || b.updatedAt?._seconds || 0);
    return bSec - aSec;
  }), [invoices]);
  const effectiveStatusFilter = controlledStatusFilter || statusFilter;

  const filtered = useMemo(() => {
    if (effectiveStatusFilter === "all") return sorted;
    return sorted.filter((row) => String(row.status || "").toLowerCase() === effectiveStatusFilter);
  }, [effectiveStatusFilter, sorted]);

  const rows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const approveSelectedInvoice = async () => {
    if (!selectedInvoice || confirmSubmitting) return;
    setConfirmSubmitting(true);
    try {
      await onApprove?.(selectedInvoice);
      setSelectedInvoice(null);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const processSelectedInvoice = async () => {
    if (!selectedInvoice || confirmSubmitting) return;
    setConfirmSubmitting(true);
    try {
      await onMoveToProcessing?.(selectedInvoice, "processing", "");
      setSelectedInvoice(null);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const selectedInvoiceKey = selectedInvoice ? `${selectedInvoice.userId}:${selectedInvoice.id}` : "";
  const confirmBusy = confirmSubmitting || (selectedInvoiceKey ? !!busyById[selectedInvoiceKey] : false);
  const selectedInvoiceStatus = String(selectedInvoice?.status || "").toLowerCase();

  useEffect(() => {
    setPage(0);
  }, [controlledStatusFilter]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2, display: "flex", flexDirection: "column" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200", color: "grey.700" }}>
            <ReceiptLongOutlinedIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ fontWeight: 600 }}>Invoices</Typography>
        </Stack>
        {showStatusFilter && (
          <TextField
            select
            size="small"
            value={effectiveStatusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              onStatusFilterChange?.(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </TextField>
        )}
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={40} />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <Box sx={{ flexGrow: 1, minHeight: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No invoices found.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>Contractor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const rowKey = `${row.userId}:${row.id}`;
                return (
                  <TableRow key={rowKey} hover sx={{ cursor: "pointer" }} onClick={() => setSelectedInvoice(row)}>
                    <TableCell>{formatDate(resolveInvoiceIssueDate(row) || row.createdAt || row.updatedAt)}</TableCell>
                    <TableCell>{row.month ? dayjs(row.month, "YYYY-MM").format("MMMM YYYY") : "—"}</TableCell>
                    <TableCell>{String(row?.seller?.name || "").trim() || resolveContractorName?.(row.userId) || "Contractor"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(row.status)} color={statusColor(row.status)} sx={{ borderRadius: 1 }} />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(Number(row.amount || 0), row.currency || "usd")}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={filtered.length}
                  page={page}
                  onPageChange={(_event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                  labelRowsPerPage="Show"
                  colSpan={5}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!selectedInvoice} onClose={() => !confirmBusy && setSelectedInvoice(null)}>
        <DialogTitle sx={{ pr: 6 }}>
          Invoice
          <IconButton
            size="small"
            onClick={() => setSelectedInvoice(null)}
            disabled={confirmBusy}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close invoice"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <InvoiceTemplate invoice={selectedInvoice} formatCurrency={formatCurrency} />
          {selectedInvoiceStatus === "open" && (
            <SlideConfirm
              disabled={confirmBusy}
              helper="Slide to confirm this invoice is approved for payment readiness."
              label="APPROVE"
              slideLabel="Slide to approve"
              tone="success"
              onConfirm={approveSelectedInvoice}
            />
          )}
          {selectedInvoiceStatus === "approved" && (
            <SlideConfirm
              disabled={confirmBusy}
              helper="Slide to move this invoice to processing. Stripe Connect will mark it paid after successful payment."
              label="PAY"
              slideLabel="Slide to pay"
              tone="success"
              onConfirm={processSelectedInvoice}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default Invoices;
